import CryptoJS from 'crypto-js';
import { 
  getStoreApiUrl, getBio11Url, isMobileApp, isMobileWeb, calcAge, toFormatDate, getHashParam, buildQueryString 
} from '@/utils/common';
import { getErrorMessage } from '@/utils/api';
import { SNS, EXCLUDE_RETURN_URL } from '@/constants/login';
import { naverLoginClientSecretKey, naverLoginClientIdKey, kakaoClientIdKey } from '@/constants/key';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// 회원조회 옵션 타입
interface SearchMemberOption {
  name?: string;
  phoneNumber?: string;
  userId?: string;
  email?: string;
  ci?: string;
  snsId?: string;
  rute?: string;
}

/**
 * 사용자 인증 데이터 설정 (페이지 이동 시)
 */
export const pageAuth = async () => {
  const authStore = useAuthStore();
  const { $getCookie, $getAccessToken } = useNuxtApp();
  
  // 로그인 체크
  if (!authStore.isLogin) {
    if (isMobileApp()) {
      const appSessId = $getCookie('APPSESSID');
      if (appSessId) {
        authStore.saveAuthenToken(appSessId);
      }
    } else {
      if ($getCookie('REFRESH_TOKEN')) await $getAccessToken();
    }
  }
};

/**
 * 로그인 체크
 * @returns 
 * {
 *   loaded: boolean, 로딩완료 여부
 *   error: boolean, 에러 여부
 *   login: boolean, 로그인 여부
 *   code: string, 결과 코드
 * }
 */

// export const loginCheck = async () => {
//   let loaded = false;
//   let error = false;
//   let login = false;
//   let code = '';

//   const { $getCookie, $sendPost } = useNuxtApp();
//   const authStore = useAuthStore();
  
//   if (
//     (isMobileApp() && !authStore.isLogin && !$getCookie('APPSESSID')) 
//     || (!isMobileApp() && (!authStore.isLogin || !$getCookie('REFRESH_TOKEN')))
//   ) {
//     return {
//       loaded: true,
//       error,
//       login,
//       code
//     };
//   }
  
//   const { data: result, error: err } = await $sendPost({
//     url: `${getStoreApiUrl('link')}/member/loginCheck`,
//     showDefaultLoading: false,
//   });
  
//   if (err) {
//     error = true;
//   } else if (result?.RESULT_CODE === '0') {
//     login = true;
//   } else if (result?.RESULT_CODE === '600') {
//     const router = useRouter();
//     router.push('/notice');
//   }
  
//   if (!err && result) code = result.RESULT_CODE;
  
//   loaded = true;

//   return {
//     loaded,
//     error,
//     login,
//     code
//   };
// };

// 앱 로그인 후처리
export const appResponseLogin = (): void => {
  if (!import.meta.client || !isMobileApp()) return;
  
  const authStore = useAuthStore();
  const router = useRouter();
  const route = useRoute();
  const { $getCookie, $setCookie } = useNuxtApp();
  
  window.responseLogin = (result: string, token: string): void => {
    //console.log('responseLogin::::::::확인::::::::', result, token);
    
    if (result === 'success') {
      if (token) {
        authStore.saveAuthenToken(token);
        // 앱에서 생성된 쿠키 APPSESSID 값을 웹에서 쿠키 APP_SESSID으로 다시 생성함
        $setCookie('APP_SESSID', token);
      }
      
      let successUrl = $getCookie('APP_SUCCESS_URL');
      if (successUrl) {
        if (successUrl.startsWith('http')) successUrl = getAbsPath(successUrl);
        
        const path = successUrl.split('?')[0];
        if (successUrl === route.fullPath) {
          router.go(0);
        } else if (path === route.path) {
          window.location.href = successUrl;
        } else {
          navigateTo(successUrl);
        }
      } else {
        router.go(0);
      }
      //goTo(successUrl ? successUrl : '/');
    } else if (result === 'fail') {
      router.push('/');
    } else if (result === 'cancel') {
      const cancelUrl = $getCookie('APP_CANCEL_URL');
      goTo(cancelUrl ? cancelUrl : '/');
    }
  }
};

// 앱 로그아웃 후처리
export const appResponseLogout = (): void => {
  if (!import.meta.client || !isMobileApp()) return;
  
  window.responseLogout = () => {
    console.log('app::::logout실행:::::');
    removeLoginSessionId();
    const route = useRoute();
    const router = useRouter();
    
    if (route.path !== '/') {
      navigateTo('/');
    } else {
      router.go(0);
    }
  }
}


/**
 * 사용자 정보 구하기(임직원 여부 포함)
 * @returns {{
*  error: boolean;   // 오류 여부
*  isStaffMall: boolean; // 임직원몰 진입 가능여부
*  isBirth: boolean; // 14세 미만여부
* }}
*/
export const getMyInfo = async (showDefaultLoading: boolean = true): Promise<{ error: boolean; data: Record<string, any>, isStaffMall: boolean; isBirth: boolean; }> => {
  const rootStore = useRootStore();
  const authStore = useAuthStore();
  const config = useRuntimeConfig();
  const { $sendGet } = useNuxtApp();
  
  let error = false;
  let data = null;
  let isStaffMall = false;
  let isBirth = false;

  if (authStore.isLogin) {
    // 사용자 정보
    const { data: result, error: err } = await useNuxtApp().$sendGet({
      url: getStoreApiUrl('myInfo'),
      showDefaultLoading,
    });
  
    if (err) {
      error = true;
    } else if (result && !['0', '301', '500'].includes(result.RESULT_CODE)){
      rootStore.toast(getErrorMessage(result?.RESULT_CODE));
    } else if (result && result.RESULT_CODE === '0') {
      data = result.DATA;
      const birthday = data.birthday ? data.birthday.replace('/-/g','') : '';
      const age = calcAge(birthday);

      // 14세 미만 체크
      if (age !== null && age < 14) {
        isBirth = true;
      }

      const { data: staffMall } = await $sendGet({
        url: `${config.public.VITE_STORE_PRODUCT_API_URL_V2}/goods/l2/categoryAccessRight/0013`,
        showDefaultLoading: false,
      });
      
      if (staffMall?.RESULT_CODE === '0') isStaffMall = staffMall.DATA;
    }
  }
  
  
  return { error, data, isStaffMall, isBirth };
};

// 회원정보 조회
export const getMemberInfo = async (showDefaultLoading: boolean = true, isFirstLogin : boolean = false): Promise<Record<string, any> | null> => {
  const memberStore = useMemberStore();

  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('mypage')}/l1/memberDetails`,
    showDefaultLoading,
  });

  if(data?.RESULT_CODE === '0') {
    memberStore.saveMemberInfo(data.DATA);
    
    //로그인시 이벤트 호출
    if (isFirstLogin) {
      const flareLaneUser = {
        membership_level : data.DATA?.memberGroupName,
      }
      await flareLaneSetTag(flareLaneUser); 
    }

    return data.DATA
  }
  memberStore.clearMemberInfo()
  return null
  // return data?.RESULT_CODE === '0' && data?.DATA.memberSeq !== -1 ? data.DATA : null;
};

//등급 확인
export const getGradeData = async (): Promise<any | null> => {
  const { data } = await useNuxtApp().$sendPost({
    url: getStoreApiUrl('mypage') + '/l1/memberGradeDetails',
    showDefaultLoading: false
  });

  if (data) {
    if (data.RESULT_CODE === '0') {
      return data.DATA;
    }
    return null;
  }
  return null;
};

/**
 * 계정 조회
 * @param body 
 * @returns data: result.DATA, code: result.RESULT_CODE
 */
export const getAccountSearch = async (body: Record<string, any>, showDefaultLoading: boolean = true): Promise<{ data: any;code: string}> => {
  const config = useRuntimeConfig();
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/${config.public.VITE_STORE_AUTH_VER}/store/auth/login/simple/accounts-search`,
    data: body,
    showDefaultLoading: false,
  });
  
  return {
    data: data?.DATA ?? null ,
    code: data?.RESULT_CODE ?? '',
  }
};

// 사용자 인증 데이터 삭제
export const removeLoginSessionId = (): void => {
  const authStore = useAuthStore();
  const memeberStore = useMemberStore();
  
  const { $setCookie } = useNuxtApp();
  
  $setCookie('APPSESSID', null);
  $setCookie('APP_SESSID', null);
  $setCookie('REFRESH_TOKEN', null);
  $setCookie('USER_ID', null);
  $setCookie('afterLoginUrl', null);
  
  authStore.clearAuthenToken();
  memeberStore.clearMemberInfo();
};

/**
 * 앱 브릿지 함수 호출
 * @param code 오류코드 - 일반적인 경우에 '' 입력
 */
export const appLoginCall = (code: string): void => {
  const { $setCookie } = useNuxtApp();
  
  if (code) {
    $setCookie('APP_SUCCESS_URL', null);
    $setCookie('APP_CANCEL_URL', null);
  }
  
  if (isAndroid()) {
    window.android.requestLogin(code);
  } else if (isIOS()) {
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.experCounselHandler) {
      let message = {
          command: "requestLogin"
      };
      window.webkit.messageHandlers.experCounselHandler.postMessage(message);
    }
  }
};

/**
 * 앱 로그인 호출
 * @param successUrl 로그인 성공 후 이동하는 url
 * @param cancelUrl 로그인 취소 시 이동하는 url
 */
export const goAppLogin = (successUrl: string | null = null, cancelUrl: string | null = null): void => {
  const { $setCookie } = useNuxtApp();
  
  $setCookie('APP_SUCCESS_URL', successUrl);
  $setCookie('APP_CANCEL_URL', cancelUrl);
  
  appLoginCall('');
};

/**
 * 웹 로그인 이동
 * @param retUrl 로그인 성공 후 이동하는 url
 * @param cancelUrl 로그인 취소 시 이동하는 url
 */
export const goWebLogin = (retUrl: string | null = null, cancelUrl: string | null = null): void => {
  const route = useRoute();
  const router = useRouter();

  let nextUrl: string | null = null;
  
  if (retUrl && !EXCLUDE_RETURN_URL.includes(retUrl)) {
    nextUrl = retUrl;
  } else if (!EXCLUDE_RETURN_URL.includes(route.path)) {
    nextUrl = retUrl ? retUrl : route.fullPath;
  }
  
  router.push({
    path: '/login',
    query: {
      nextUrl : nextUrl,
      cancelUrl: cancelUrl ? cancelUrl : '/',
    },
  });
};

/**
 * 로그인 화면 이동(confirm 창 사용)
 * @param successUrl 로그인 성공 후 이동하는 url
 * @param cancelUrl  로그인 취소 시 이동하는 url
 */
export const showLoginPage = (successUrl: string | null = null, cancelUrl: string | null = null): void => {
  const rootStore = useRootStore();

  //웹과 앱의 문구처리
  const isApp = isMobileApp();
  const title = isApp ? '로그인 후 사용 가능해요' : '로그인 하시겠습니까?';
  const message = isApp ? '로그인 하시겠어요?' : '로그인이 필요한 페이지입니다.';
  const cancelText = isApp ? '취소' : '닫기';
  const confirmText = isApp ? '확인' : '로그인';
  
  // Confirm 창 열기
  rootStore.openConfirm(
    title,
    message,
    cancelText,
    confirmText,
    // 취소 버튼 동작
    () => {
      if (cancelUrl) {
        goTo(cancelUrl);
      } else {
        rootStore.resetConfirm();
      }
    },
    // 확인 버튼 동작
    () => {
      if (isApp) {
        goAppLogin(successUrl, cancelUrl);
      } else {
        goWebLogin(successUrl, cancelUrl);
      }
    },
  );
};

/**
 * 로그인 화면 이동(confirm 창 사용)
 * @param successUrl 로그인 성공 후 이동하는 url
 * @param cancelUrl  로그인 취소 시 이동하는 url
 */
export const showLoginCouponPage = (successUrl: string | null = null, cancelUrl: string | null = null): void => {
  const rootStore = useRootStore();
  rootStore.openConfirm(
    '로그인 하시겠습니까?',
    '로그인이 필요한 페이지입니다.',
    '닫기',
    '로그인',
    () => {
      if (cancelUrl) goTo(cancelUrl);
    },
    () => {
      if (isMobileApp()) {
        goAppLogin(successUrl, cancelUrl);
      } else {
        goWebLogin(successUrl, cancelUrl);
      }
    },
  );
};

/**
 * 로그인 화면 이동
 * @param successUrl 로그인 성공 후 이동하는 url
 * @param cancelUrl  로그인 취소 시 이동하는 url
 */
export const showLoginPageWithoutConfirm = (successUrl: string | null = null, cancelUrl: string | null = null): void => {
  if (isMobileApp()) {
    goAppLogin(successUrl, cancelUrl);
  } else {
    goWebLogin(successUrl, cancelUrl);
  }
};

// mypage 이동
export const goMyPage = async (): Promise<void> => {
  const authStore = useAuthStore();

  if (authStore.isLogin) {
    navigateTo('/mypage');
  } else {
    removeLoginSessionId();
    showLoginPage('/mypage');
  }
};

// 네이버 로그인 셋팅
export const naverLoginSetting = (): void => {
  const config = useRuntimeConfig();
  
  // 네이버 로그인 설정
  nextTick(() => {
    const naverLogin = new window.naver.LoginWithNaverId({
      clientId: naverLoginClientIdKey(),
      callbackUrl: `${checkURL()}/callback`,
      loginButton: { color: "green", type: 1, height: 60 },
      isPopup: true,
    });
    naverLogin.init(); 
  });  
};

//진입 URL 체크
export const checkURL = (): any => {
  const config = useRuntimeConfig();
  let resultURL = config.public.VITE_TTOBAK_MOBILE_URL as string; // 기본값 설정

  // SSR 환경
  if (import.meta.env.SSR) {
    const headers = useRequestHeaders();
    const host = headers['host'] || '';

    if (host.includes("desimone.co.kr")) {
      resultURL = config.public.VITE_DESIMONE_MOBILE_URL as string;
    } else if (host.includes("ttobakcare.com")) {
      resultURL = config.public.VITE_TTOBAK_MOBILE_URL as string;
    } else {
      resultURL = config.public.VITE_TTOBAK_MOBILE_URL as string;
    }
  } else {
    // CSR 환경
    const currentURL = window.location.hostname;
    if (currentURL.includes("desimone.co.kr")) {
      resultURL = config.public.VITE_DESIMONE_MOBILE_URL as string;
    } else if (currentURL.includes("ttobakcare.com")) {
      resultURL = config.public.VITE_TTOBAK_MOBILE_URL as string;
    } else {
      resultURL = config.public.VITE_TTOBAK_MOBILE_URL as string;
    }
  }

  return resultURL;
};


// 네이버 로그인 이동
export const goNaverLogin = (): void => {
  const config = useRuntimeConfig();
  const route = useRoute();

  const redirectUri = encodeURIComponent(`${checkURL()}/callback`);
  const nextUrl = route.query.nextUrl ? encodeURIComponent(`${route.query.nextUrl}`) : encodeURIComponent(route.fullPath);
  
  const url = `https://nid.naver.com/oauth2.0/authorize?response_type=token&client_id=${naverLoginClientIdKey()}&redirect_uri=${redirectUri}&state=${nextUrl}`;
  navigateTo(url, { external: true })
};

/**
 * 네이버 로그인 이동(연동 해제)
 * @param nextKakao 카카오 연동 해제 여부(네이버 연동 해제 이후)
 * @param userId 사용자 id
 */
export const goNaverLoginForWithdrawal = (nextKakao: boolean, userId: string): void => {
  const config = useRuntimeConfig();

  const redirectUri = encodeURIComponent(`${checkURL()}/callback`);
  // {withdrawal[sp]카카오 연동 해제 여부[sp]사용자 id}
  const state = encodeURIComponent(`withdrawal[sp]${nextKakao ? 'Y' : 'N'}[sp]${userId}`);
  
  const loginUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=token&client_id=${naverLoginClientIdKey()}&redirect_uri=${redirectUri}&state=${state}`;
  navigateTo(loginUrl, { external: true });
};

// 네이버 로그인 이동
export const goKakaoLogin = (): void => {
  const config = useRuntimeConfig();
  const route = useRoute();
  
  const redirectUri = encodeURIComponent(`${checkURL()}/callbackKakao`);
  const nextUrl = route.query.nextUrl ? encodeURIComponent(`${route.query.nextUrl}`) : encodeURIComponent(route.fullPath);
  const url = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${kakaoClientIdKey()}&redirect_uri=${redirectUri}&state=${nextUrl}`;
  navigateTo(url, { external: true })
};

/**
 * 카카오 로그인 이동(연동 해제)
 * @param userId 사용자 id
 */
export const goKakaoLoginForWithdrawal = (userId: string): void => {
   const config = useRuntimeConfig();
   const redirectUri = `${checkURL()}/callbackWithDrawalKakao`;
   const state = encodeURIComponent(`withdrawal[sp]${userId}`);
   const loginUrl = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${kakaoClientIdKey()}&redirect_uri=${redirectUri}&state=${state}`;
   navigateTo(loginUrl, { external: true });
};

/**
 * 카카오톡 access token 가져오기
 * @param code 
 * @param redirect_uri 
 * @returns access token
 */
export const getAcessTokenForKakao = async (code: string, redirect_uri: string): Promise<string> => {
  const data: Record<string, string> = {
    grant_type: 'authorization_code', 
    client_id: `${kakaoClientIdKey()}`,
    redirect_uri,
    code,
  };
  
  const url = 'https://kauth.kakao.com/oauth/token';
  const body = Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
    
  let result: Record<string, any> | null = null;
  
  try {
    result = await $fetch<Record<string, any>>(url, {
      method: 'POST',
      headers: {
        'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
      body,
    });
  } catch (error) {
    console.error('kakao token', error);
  }
  
  return result?.access_token ?? '';
};

// 애플 로그인 이동
export const goAppleLogin = (): void => {
  const config = useRuntimeConfig();
  const route = useRoute();
  
  const nextUrl = route.query.nextUrl ? encodeURIComponent(`${route.query.nextUrl}`) : encodeURIComponent(route.fullPath);
  const query: Record<string, any> = {
    client_id: config.public.VITE_APPLE_LOGIN_CLIENT_ID,
    redirect_uri: `${checkURL()}/callbackApple`,
    response_type: 'code id_token',
    state: nextUrl,
    response_mode: 'fragment',
  };
  
  const queryStr = Object.keys(query)
    .map(key => `${key}=${encodeURIComponent(query[key])}`)
    .join('&');
  const url = `https://appleid.apple.com/auth/authorize?${queryStr}`;
  navigateTo(url, { external: true });
};

export const setResponseLogin = (url: string | null): void => {
  if (!url) return;
  window.location.href = url;
};

// SNS 회원가입 후 처리(router replace처리)
export const afterLoginForSNS = (nextUrl: string | undefined): void => {
  replaceUrl(nextUrl ? nextUrl : '/');
};

/**
 * Naver Profile 검색
 * @param token 
 * @param showDefaultLoading 
 * @returns result.DATA
 */
export const searchNaverProfile = async (token: string, showDefaultLoading: boolean = true): Promise<Record<string, any> | null> => {
  const { data: result } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/naver/profile/search`,
    data: {
      token
    },
    showDefaultLoading,
  });
  
  return result && result.RESULT_CODE === '0' ? (result.DATA ?? null): null;
};

// Kakao Profile 검색
export const searchKakaoProfile = async (token: string): Promise<Record<string, any> | null> => {
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/kakao/profile/search`,
    data: {
      token
    },
    showDefaultLoading: false,
  });
  
  return data?.RESULT_CODE === '0' ? (data.DATA ?? null): null;
  /* let result: Record<string, any> | null = null;
  
  try {
    result = await $fetch<Record<string, any>>('https://kapi.kakao.com/v2/user/me', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    });
  } catch (error) {
    console.error('searchKakaoProfile', error);
  }

  return result;  */
};

// SNS 약관 동의 정보 조회
export const getSNSUserTerms = async (route: string, token: string, showDefaultLoading: boolean = true): Promise<{ mailing: boolean; sms: boolean, appPush: boolean, termsSeq?: number[] }> => {
  console.log('route:::::::::::::::', route);
  console.log('token:::::::::::::::', token);

  const result: {
    mailing: boolean;
    sms: boolean;
    appPush: boolean;
    termsSeq: number[];
  } = {
    mailing: false,
    sms: false,
    appPush: false,
    termsSeq: [],
  };
  
  if (route === 'naver') {
    const { data } = await useNuxtApp().$sendPost({
      url: `${getStoreApiUrl('auth')}/naver/profile/search-agreement`,
      data: {
        token,
      },
      showDefaultLoading,
    });
    
    if (data && data.RESULT_CODE === '0') {
      const agreeInfos = data.DATA?.agreementInfos ? (data.DATA.agreementInfos as Array<Record<string, any>>) : null;
      
      const emailAgreeYn = agreeInfos ? agreeInfos.find(term => term.termCode === 'Email') : null;
      const smsAgreeYn = agreeInfos ? agreeInfos.find(term => term.termCode === 'SMS') : null;
      const pushAgreeYn = agreeInfos ? agreeInfos.find(term => term.termCode === 'PUSH') : null;
      
      if (emailAgreeYn && Object.prototype.hasOwnProperty.call(emailAgreeYn, 'agreeDate')) result.mailing = true;
      
      if (smsAgreeYn && Object.prototype.hasOwnProperty.call(smsAgreeYn, 'agreeDate')) result.sms = true;
      
      if (pushAgreeYn && Object.prototype.hasOwnProperty.call(pushAgreeYn, 'agreeDate')) result.appPush = true;
      
      result.termsSeq = agreeInfos ? agreeInfos.filter(term => !isNaN(Number(term.termCode))).map(term => Number(term.termCode)) : [];
    }
  } else if (route === 'kakao') {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
    };
    
    let data: Record<string, any> | null = null;
    
    try {
      data = await $fetch<Record<string, any>>('https://kapi.kakao.com/v2/user/service_terms', {
        method: 'POST',
        headers,
      });
    } catch (error) {
      console.error('getSNSUserTerms:kakao', error);
    }
    
    if (data) {
      const serviceTerms = data?.service_terms ? (data?.service_terms as Array<Record<string, any>>): null;
      
      const emailAgreeYn = serviceTerms ? serviceTerms.find(term => term.tag === 'Email') : null;
      const smsAgreeYn = serviceTerms ? serviceTerms.find(term => term.tag === 'SMS') : null;
      const pushAgreeYn = serviceTerms ? serviceTerms.find(term => term.tag === 'PUSH') : null;

      if (emailAgreeYn && Object.prototype.hasOwnProperty.call(emailAgreeYn, 'agreed')) result.mailing = emailAgreeYn.agreed;
      
      if (smsAgreeYn && Object.prototype.hasOwnProperty.call(smsAgreeYn, 'agreed')) result.sms = smsAgreeYn.agreed;
      
      if (pushAgreeYn && Object.prototype.hasOwnProperty.call(pushAgreeYn, 'agreed')) result.appPush = pushAgreeYn.agreed;

      result.termsSeq = serviceTerms? serviceTerms.filter(term => !isNaN(Number(term.tag))).map(term => Number(term.tag)) : [];
    }
  }
  
  return result;
};

/**
 * 기존회원 조회
 * @param {SearchMemberOption} options 
 * @returns code: result.RESULT_CODE / data: result.DATA
 * 
 * code : 502 (일치하는 회원정보 있음)
 */
export const searchMember = async (options: SearchMemberOption, showDefaultLoading: boolean = true): Promise<{ code: string; data: Record<string, any> | null}> => {
  const { data: result } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/member/checkExistMember`,
    data: options,
    showDefaultLoading,
  });
  
  return {
    code: result?.RESULT_CODE ?? '',
    data: result?.DATA ?? null,
  };
};

/**
 * 회원정보 조회
 * @returns code: result.RESULT_CODE / data: result.DATA
 */
export const getDetailMemberInfo = async (): Promise<{ code: string; data: Record<string, any> | null}> => {
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('mypage')}/l1/memberInfoDetails`,
    showDefaultLoading: false,
  });
  
  return {
    code: data?.RESULT_CODE ?? '',
    data: data?.DATA ?? null,
  };
};

/**
 * 추천 가능 여부 조회
 * @param cellPhone 
 * @param recommendId 
 * @returns exist : 추천인 존재여부, can : 추천 가능 여부
 */
export const checkRecommend = async (cellPhone: string, recommendId: string, showDefaultLoading: boolean = true): Promise<{ exist: boolean; can: boolean }> => {
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/member/canRecommend`,
    data: {
      cellPhone,
      recommendId,
    },
    showDefaultLoading,
  });
  
  return {
    exist: data?.RESULT_CODE === '0' ? true : false,
    can: data?.RESULT_CODE === '0' ? data.DATA?.canRecommend : false,
  };
};

/**
 * 일반회원 SNS 연동 가능 여부 확인
 * @param route SNS 이름
 * @param phoneNumber 휴대폰번호
 * @param userId 사용자 ID
 * @returns true (가능), false (불가능)
 */
export const checkSNSLink = async (route: string, phoneNumber: string, userId: string): Promise<boolean> => {
  if (!route || !['naver', 'kakao'].includes(route) || !phoneNumber || !userId) return false;
  
  const { code, data } = await searchMember({ phoneNumber });
  
  if (code !== '502' || !data) return false;
  
  const userList = data.userList as Array<Record<string, any>>;
  const findUser = userList.find(user => user.rute.includes('none') && !user.rute.includes(route) && user.userid == userId);
  if (findUser) {
    return true;
  }
  
  return false;
};

/**
 * 계정이 SNS에 연동되어 있는지 확인
 * @param snsId SNS ID
 * @param route SNS name (naver, kakao)
 * @param userId 사용자 ID
 * @returns -1 (유효하지 않은 인수), 0 (존재하지 않음), 1 (연동되어 있음), 2 (snsId에 연동되었으나 userId와 연동되어 있지 않음)
 */
export const checkSNSLinked = async (snsId: string, route: string, userId: string): Promise<number> => {
  if (!snsId || !route || !['naver', 'kakao'].includes(route) || !userId) return -1;
  
  const { code, data } = await searchMember({
    snsId,
    rute  : route
  }, false);
  if (code !== '502') return 0;
  
  if (Array.isArray(data?.userList)) {
    const userList = data?.userList as Record<string, any>[];
    const findUser = userList.find(user => user.userid === userId);
    if (findUser) return 1;
  }
  
  return 2;
};

/**
 * 기가입자 정보 확인
 * @param snsUserInfo 
 * @return true (가입정보 존재), false (가입정보 없음)
 */
export const checkExistMemberByPhone = async (snsUserInfo: SnsUserInfo): Promise<boolean> => {
  if (!snsUserInfo.phoneNumber) return false;
  
  const rootStore = useRootStore();
  const authStore = useAuthStore();
  const router = useRouter();
  
  const { code, data } = await searchMember({ phoneNumber : snsUserInfo.phoneNumber });
  
  if (code !== '502') return false;
  
  if (!data?.userList) return false;
  
  const userList = data?.userList as Array<Record<string, any>>;
  
  type RouteNameKeys = keyof typeof SNS.ROUTE_NAME;
  
  // 일반 회원존재 여부
  const noneSnsUser = userList.some(item => item.rute.includes('none'));
  // 동일한 SNS 연동 여부
  const findSameSns = userList.some(item => item.rute.includes(snsUserInfo.route));
  // 다른 SNS 이름
  const findOtherSns = [
    ...new Set(
      userList.flatMap(user => user.rute)
              .filter(route => route !== "none" && route !== snsUserInfo.route)
    )].map(route => SNS.ROUTE_NAME[route as RouteNameKeys]).join(' 및 ');

  if (findSameSns) {
    if (['naver', 'kakao'].includes(authStore.snsToken.route) && authStore.snsToken.accessToken) await unlinkSNS(authStore.snsToken.route as ('naver' | 'kakao'), authStore.snsToken.accessToken);
    
    rootStore.showMessageDialog(`다른 ${SNS.ROUTE_NAME[snsUserInfo.route]} 계정으로 연동되어 있습니다.`, '확인', () => {
      router.replace('/');
    });
  } else if (noneSnsUser) {
    router.replace({
      path: '/member/linksns', 
      query : 
      {
        route : snsUserInfo.route, 
        snsId : snsUserInfo.snsID, 
        snsEmail : snsUserInfo.email, 
        phoneNumber : snsUserInfo.phoneNumber,
        nextUrl: snsUserInfo.nextUrl,
      }
    });
  } else if (findOtherSns) {
    if (['naver', 'kakao'].includes(authStore.snsToken.route) && authStore.snsToken.accessToken) await unlinkSNS(authStore.snsToken.route as ('naver' | 'kakao'), authStore.snsToken.accessToken);
    
    rootStore.showMessageDialog(`회원님께서는 ${findOtherSns} 간편로그인으로 등록되어 있습니다.`, '확인', () => {
      router.replace('/');
    });
  } else {
    return false;
  }
  
  return true;
};

/**
 * 아이디, 비밀번호로 로그인
 * @param id  아이디
 * @param pwd 비밀번호
 * @param isTemp 임시 비밀번호 여부
 * @param showDefaultLoading 로딩 이미지 노출여부
 * @returns true (성공), false (실패)
 */
export const loginByIDAndPWD = async (id: string, pwd: string, isTemp: boolean = false, showDefaultLoading: boolean = true): Promise<boolean> => {
  const rootStore = useRootStore();
  const authStore = useAuthStore();
  const config = useRuntimeConfig();
  const { $setCookie, $sendPost } = useNuxtApp();
  
  const { data } = await $sendPost({
    url: `${getStoreApiUrl('auth')}/${config.public.VITE_STORE_AUTH_VER}/store/auth/login/id`,
    data: {
      userId: id,
      userPassword: pwd,
      isTempPassword: isTemp,
    },
    showDefaultLoading,
  });
  
  if (data?.RESULT_CODE === '402') {
    rootStore.showMessageDialog('아이디 또는 비밀번호가 일치하지 않습니다.', '확인');
    return false;
  } else if (data?.RESULT_CODE !== '0') {
    rootStore.toast(getErrorMessage(data?.RESULT_CODE ?? ''));
    return false;
  }
  
  authStore.saveAuthenToken(data.DATA?.encodedJwt ?? '');
  $setCookie('REFRESH_TOKEN', data.DATA?.refreshToken ?? '');
  $setCookie('USER_ID', data.DATA?.userId ?? '');
  
  // 로그 이벤트 처리
  loginEventSend(data.DATA, 'none');
    
  return true;
};

/**
 * SNS 로그인 처리
 * @param snsUserInfo SNS 정보
 * @return true (성공), false (실패)
 */
export const loginSNS = async (snsUserInfo: SnsUserInfo): Promise<boolean> => {
  const rootStore = useRootStore();
  const authStore = useAuthStore();
  const router = useRouter();
  const config = useRuntimeConfig();
  const { $setCookie, $sendPost } = useNuxtApp();
  
  let url = `${getStoreApiUrl('auth')}/${config.public.VITE_STORE_AUTH_VER}/store/auth/login/${snsUserInfo.route}`;
  
  if (snsUserInfo.nextUrl) {
    try {
      const nextUrlSearch = snsUserInfo.nextUrl.split('?');
      const params = new URLSearchParams(nextUrlSearch.length > 1 ? nextUrlSearch[1] : '');
      if (params.has('click_key') && params.get('click_key')) url += `?click_key=${params.get('click_key')}`;
    } catch (error) {
      console.error('loginSNS nextUrl', error)
    }
  }
  
  const { data: result } = await $sendPost({
    url,
    data: {
      snsID: snsUserInfo.snsID
    },
    showDefaultLoading: true,
  });
  
  // SNS 회원 가입정보가 없을 경우
  if (result?.RESULT_CODE === '101') {
    $setCookie(SNS.COOKIE_NAME[snsUserInfo.route], snsUserInfo.snsID);
    rootStore.showMessageDialog('가입된 정보가 없습니다. 회원가입 페이지로 연결됩니다', '확인', () => {
      router.replace({ 
        path: '/member/register', 
        query: { 
          route: snsUserInfo.route,
          nextUrl: snsUserInfo.nextUrl ? snsUserInfo.nextUrl : '',
        }
      })
    });
    return false;
  } else if (result?.RESULT_CODE === '513') {
    
    // 애플
    if (snsUserInfo.route === 'apple') {
      router.replace({
        path: '/member/linksns',
        query: { 
          route: snsUserInfo.route, 
          snsId: snsUserInfo.snsID, 
          phoneNumber : snsUserInfo.phoneNumber,
          nextUrl: snsUserInfo.nextUrl ? snsUserInfo.nextUrl : '',
        }
      });
      // 네이버, 카카오
    } else if (['naver', 'kakao'].includes(snsUserInfo.route)) {
      if (await checkExistMemberByPhone(snsUserInfo)) return false;
      
      $setCookie(SNS.COOKIE_NAME[snsUserInfo.route], snsUserInfo.snsID);
      
      // snsID, 생일, 성별, 이름, 휴대폰, CI(개발, 로컬은 제외) 가 존재할 경우 가입처리
      if (snsUserInfo.snsID && snsUserInfo.birthDay && snsUserInfo.gender && snsUserInfo.name && snsUserInfo.phoneNumber && ( 
        snsUserInfo.ci || (config.public.VITE_ENV_TYPE !== 'production' && config.public.VITE_ENV_TYPE !== 'stage')
      )) {
        snsRegist(snsUserInfo);
      } else {
        router.replace({
          path :'/member/register', 
          query: { 
            route: snsUserInfo.route,
            nextUrl: snsUserInfo.nextUrl ? snsUserInfo.nextUrl : '',
          }
        });
      }
    }
    return false;
  } else if (result?.RESULT_CODE !== '0') {
    rootStore.toast(getErrorMessage(result?.RESULT_CODE ?? ''));
    return false;
  }
  
  // 로그인 처리
  authStore.saveAuthenToken(result.DATA.encodedJwt ?? '');
  $setCookie('REFRESH_TOKEN', result.DATA.refreshToken ?? '');
  $setCookie('USER_ID', result.DATA.userId ?? '');
  
  // 로그 이벤트 처리
  loginEventSend(result.DATA, snsUserInfo.route);

  regAgreePolicyTerms(snsUserInfo);
  
  return true;
};

/**
 * SNS 사용자약관동의 여부 가져오기 
 * @param route 
 * @param token 
 * @param snsUserInfo 
 */
export const regAgreePolicyTerms = async (snsUserInfo: SnsUserInfo): Promise<void> => {

  if (snsUserInfo.termsSeq) {
    const termsSeqNumbers = snsUserInfo.termsSeq.map(seq => parseInt(seq, 10)).filter(Number.isFinite);
    await regAgreeTerms(termsSeqNumbers , 'PRIVACY', 'Y');
  }
}

/**
 * SNS 연동 처리
 * @param options 
 * @returns code : result.RESULT_CODE
 */
export const linkSNS = async (options: LinkSnsOption, showDefaultLoading: boolean = true): Promise<{ code: string }> => {
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/member/noSign/linkSNS`,
    data: options,
    showDefaultLoading,
  });
  
  return {
    code: data?.RESULT_CODE ?? '',
  };
};

/**
 * 일반 회원가입
 * @param body 
 * @returns code : result.RESULT_CODE / data : result.DATA
 */
export const signUp = async (body: Record<string, any>, showDefaultLoading: boolean = true): Promise<{ code: string, data: Record<string, any> | null }> => {
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/member/signUp`,
    data: body,
    showDefaultLoading,
  });
  
  return { 
    code: data?.RESULT_CODE ?? '',
    data: data?.DATA ?? null,
  };
};

// SNS 가입 처리
export const snsRegist = async (snsUserInfo: SnsUserInfo): Promise<void> => {
  const snsRegistState = useState<SnsUserInfo | null>('snsRegistState');
  
  // 가입 진행중인 체크
  if (snsRegistState.value === snsUserInfo) return;
  snsRegistState.value = snsUserInfo;
  
  const { $setCookie, $sendPost } = useNuxtApp();
  const router = useRouter();
  const route = useRoute();
  const rootStore = useRootStore();
  
  try {
    const snsID = ['naver', 'kakao'].includes(snsUserInfo.route) ? (snsUserInfo.snsID ?? null) : null;
    const name = snsUserInfo.name?.replace(/\s/g, '') ?? '';
    const body: Record<string, any> = {
      password: '',
      birthDay: snsUserInfo.birthDay ?? '',
      gender: snsUserInfo.gender ?? '',
      name,
      phoneNumber: snsUserInfo.phoneNumber,
      ci: snsUserInfo.ci ?? '',
      mailing: snsUserInfo.mailing ?? '',
      sms: snsUserInfo.sms ?? '',
      appPush: snsUserInfo.appPush ?? '',
      termsSeq : snsUserInfo.termsSeq ?? [],
      rute: snsUserInfo.route,
      platform: 'M',
      email: snsUserInfo.email ?? '',
    };
    
    if (snsID) body.snsID = snsID;
    
    // SNS 회원가입
    const { data: result } = await $sendPost({
      url: `${getStoreApiUrl('auth')}/member/signUpSNS`,
      data: body,
      showDefaultLoading: true,
    });
    
    if (result?.RESULT_CODE === '506') {
      $setCookie(SNS.COOKIE_NAME[snsUserInfo.route], snsUserInfo.snsID);
      router.push({ 
        path: '/member/register', 
        query: { 
          rute: snsUserInfo.route,
          nextUrl: snsUserInfo.nextUrl,
        }
      });
      return;
    } else if (result?.RESULT_CODE === '502') {
      checkExistMemberByPhone(snsUserInfo);
      return;
    } else if (result?.RESULT_CODE === '101' || result?.RESULT_CODE === '513') {
      const { code } = await signUp(body);
      if (code === '0') {
        if (!await loginSNS(snsUserInfo)) return;
        
        rootStore.showMessageDialog('회원가입이 완료되었습니다.' ,'확인', () => {
          afterLoginForSNS(snsUserInfo.nextUrl);
        });
      } else {
        rootStore.toast(getErrorMessage(code ?? ''));
      }
      return;
    } else if (result?.RESULT_CODE !== '0') {
      rootStore.toast(getErrorMessage(result?.RESULT_CODE ?? ''));
      return;
    }

    // flareLane setId
    await flareLaneSetUserId(result?.DATA.memberSeq);

    const interNationalPhoneNumber = parsePhoneNumberFromString( body?.phoneNumber, 'KR');
    // flareLane setUserAttributes
    const userAttributes : Record<string, any> = {
      name : result?.DATA.memberName || '',
      phoneNumber: interNationalPhoneNumber?.number || '',
      dob : result?.DATA.birthday,
      country: interNationalPhoneNumber?.country || 'kr',
      language: "ko",
      timeZone: "Asia/Seoul"
    }
    if (body?.email !== null || body?.email !== '') {
      userAttributes.email = body?.email;
    }
    console.log('test:::::::::::::::::userAttributes', userAttributes );
    await flareLaneSetUserAttributes(userAttributes)
    
    // SNS 회원가입 성공
    airbridgeSetFieldUser(result?.DATA.memberId);
    airbridgeSendEvent('airbridge.user.signup', SNS.ROUTE_NAME[snsUserInfo.route], 'social_login');
    
    const amplitudeDate = toFormatDate(new Date(), "YYYY-MM-DD");
    const amplitudeMonth = toFormatDate(new Date(), "YYYY-MM");
          
    let age = calcAge(snsUserInfo.birthDay ?? '') ?? 0;

    const ageRange = Math.floor(age / 10) * 10 + '대';
    
    const mktMessage = [];
    if (snsUserInfo.mailing) mktMessage.push('email');
    if (snsUserInfo.sms) mktMessage.push('sms');
    if (snsUserInfo.appPush) mktMessage.push('push');
    const mkt_message_string = mktMessage.join(', ');

    //sns가입 > 마케팅 약관 체크 했는지 여부
    const hasMarketingPolicy = (import.meta.env.VITE_ENV_TYPE === 'local' || import.meta.env.VITE_ENV_TYPE === 'test') ? body.termsSeq.map(String).includes('17') : body.termsSeq.map(String).includes('67') ;
    
    //amplitude이벤트
    const userProps = {
      signup_date: amplitudeDate,
      signup_month: amplitudeMonth,
      mkt_message: mkt_message_string,
      gender: snsUserInfo.gender === 'F' ? 'female' : 'male',
      age: age,
      age_range: ageRange,
      sms_allowed: snsUserInfo?.sms  ? 'Y' : 'N',
      push_allowed: snsUserInfo?.appPush ? 'Y' : 'N',
    };    
    const eventProps = {
      type: SNS.ROUTE_NAME[snsUserInfo.route]
    };
          
    amplitudeSendEvent("complete_signup", eventProps, userProps);
    await flareLaneTrackEvent('complete_signup', eventProps);
          
    const flareLaneUser : Record<string, any> = {
      user_id : result?.DATA.memberId,
      member_seq : result?.DATA.memberSeq,
      phoneNumber: body?.phoneNumber || '',
      gender: snsUserInfo.gender === 'F' ? 'female' : 'male',
      age_range: ageRange,
      signup_date : amplitudeDate,
      birth_date : snsUserInfo.birthDay,
      marketing_consent : hasMarketingPolicy ? 'Y' : 'N',
      push_allowed: snsUserInfo.appPush ? 'Y' : 'N',
      sms_allowed: snsUserInfo.sms ? 'Y' : 'N',
      email_allowed : snsUserInfo.mailing ? 'Y' : 'N',
      member_status : 'done',
      user_name : result?.DATA.memberName || ''
    }
    if (snsUserInfo.sms) {
      flareLaneUser.smsAgreeDate = amplitudeDate;
    }

    await flareLaneSetTag(flareLaneUser);
          
    const login = await loginSNS(snsUserInfo);
    if (!login) return;
    
    $setCookie(SNS.COOKIE_NAME[snsUserInfo.route], null);
    rootStore.showMessageDialog('회원가입이 완료되었습니다.', '확인', () => {
      afterLoginForSNS(snsUserInfo.nextUrl);
    });
  } catch (error) {
    console.error('snsRegist', error);
  } finally {
    snsRegistState.value = null;
  }
};

/**
 * SNS 연동해제
 * @param route SNS 이름 
 * @param token token
 * @return true (성공), false (실패)
 */
export const unlinkSNS = async (route: ('naver' | 'kakao'), token: string): Promise<boolean> => {
  if (!token) return false;
  
  const config = useRuntimeConfig();
  
  try {
    if (route === 'naver') {
      const url = `${config.public.VITE_NAVER_LOGIN_API}?client_id=${naverLoginClientIdKey()}&client_secret=${naverLoginClientSecretKey()}&access_token=${token}&grant_type=delete`;
      await $fetch(url, { method: 'GET', mode: 'no-cors' });
      return true;
    } else {
      await $fetch(`${config.public.VITE_KAKAO_UNLINK_API}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
        }
      });
      return true;
    }
  } catch (error) {
    console.error('unlinkSNS error', error);
    return false;
  }
};

/**
 * 본인인증 요청
 * @param isRetry 재요청여부
 * @param body 요청본문
 * @returns code : result.RESULT_CODE / data : result.DATA
 */
export const requestUserMobileAuth = async (isRetry: boolean, body: Record<string, any>, showDefaultLoading: boolean = true): Promise<{ code: string, data: Record<string, any> }> => {
  const config = useRuntimeConfig();
  const url = `${getStoreApiUrl('auth')}/${config.public.VITE_STORE_AUTH_VER}/okhilda/mobile/auth${isRetry ? '/sms/retry' : '/user'}`;
  const { data } = await useNuxtApp().$sendPost({
    url,
    data: body,
    showDefaultLoading,
  });
  
  return { 
    code: data?.RESULT_CODE ?? '',
    data: data?.DATA,
  };
};

/**
 * 본인인증 인증번호 확인
 * @param body 
 * @returns code : result.RESULT_CODE / data : result.DATA
 */
export const verifyUserMobileAuth = async (body: Record<string, any>, showDefaultLoading: boolean = true): Promise<{ code: string, data: Record<string, any> }> => {
  const config = useRuntimeConfig();
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('auth')}/${config.public.VITE_STORE_AUTH_VER}/okhilda/mobile/auth/result`,
    data: body,
    showDefaultLoading,
  });
  
  return {
    code: data?.RESULT_CODE ?? '',
    data: data?.DATA,
  };
};

/**
 * 본인인증 후처리
 * @param code 오류코드
 * @param reset reset 함수
 */
export const afterSci = (code: string, reset: () => void): void => {
  const rootStore = useRootStore();
  
  if (code === "304") {
    rootStore.showMessageDialog("본인인증 정보를 다시 확인해 주세요.", "확인");
  } else if (code === "305") { 
    rootStore.showMessageDialog("입력 가능한 횟수(5회)가 초과되어 본인인증을 다시 시도해 주세요.", "확인");
    reset();
  } else if (code === "306") {
    rootStore.showMessageDialog("인증번호 재 발송 시간이 초과되어 본인인증을 다시 시도해 주세요", "확인");
    reset();
  } else if (code === "307") {
    rootStore.showMessageDialog("인증번호 발송은 최대 3회까지 가능해요. 본인인증을 다시 시도해 주세요.", "확인");
    reset();
  } else if (code === "308") {
    rootStore.showMessageDialog("인증번호를 다시 확인해 주세요", "확인");
  } else if (code === "309") {
    rootStore.showMessageDialog("1일 최대 5회까지 인증시도가 가능해요. 다음에 다시 시도해 주세요.", "확인");
  } else if (code === "998") {
    rootStore.showMessageDialog("시스템 오류로 고객센터로 문의해 주세요.", "확인");
  } else {
    rootStore.toast(getErrorMessage(code ?? ''));
  }
};

// 로그아웃
export const logout = async (): Promise<void> => {
  const authStore = useAuthStore();
  const router = useRouter();
  const route = useRoute();
  
  await airbridgeSendEvent("airbridge.user.signout");
  airbridgeclearUser();
  amplitudeClearUser();
  
  if (authStore.isLogin) {
    removeLoginSessionId();
  };
  
  if (isMobileApp()) {
    if (isAndroid()) {
      window.android.logout()
    } else if (isIOS()) {
      window.location.href = "jscall://logout";
    }
  }
  
  if (route.path === '/') {
    window.location.reload();
  } else {
    router.push('/');
  }
};

/**
 * 탈퇴 - 등급.혜택 정보 가져오기
 * @returns code : result.RESULT_CODE / data : reslut.DATA
 */
export const getLeaveInfoDetail = async (): Promise<{ code: string; data: Record<string, any> | null}> => {
  const { data } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('mypage')}/l1/memberLeaveDetails`,
    showDefaultLoading: false,
  });
  
  return {
    code: data?.RESULT_CODE ?? '',
    data: data?.DATA ?? null,
  };
};

/**
 * 탈퇴처리
 * @param body .withdrawalReason : 탈퇴 사유 코드, .withdrawalContent : 탈퇴 내용
 * @returns true : 성공, false : 실패
 */
export const memberWithdrawal = async (body: { withdrawalReason: string, withdrawalContent: string}): Promise<boolean> => {
  localStorage.removeItem('withdrawal');
  
  const rootStore = useRootStore();
  const router = useRouter();
  
  const { data: result } = await useNuxtApp().$sendPost({
    url: `${getStoreApiUrl('mypage')}/l1/memberWithdrawal`,
    data: body,
  });
  
  if (result?.RESULT_CODE === '510') {
    const { subscription, order } = result.data;
    const items = [ subscription, order];
    
    let message = '';
    //정기구독만 있는 경우
    if (subscription && !order) {
      const txt = items.filter(item => item).join(', ');
      message = `${txt}을 이용 중에 계시네요!<br/>구독을 해지 후 탈퇴가 가능합니다.`;
    //주문건만 있는 경우
    } else if (order && !subscription) {
      message = `아직 배송완료되지 않은 주문 ${order} 건이 존재해요!<br/>배송완료 이후 탈퇴가 가능합니다.`;
    } else {
      const txts = [];
      if (subscription) {
        txts.push(subscription);
      }
      if (order) {
        txts.push(`주문 건`);
      }
      const txt = txts.join(', ');
      message = `${txt}이 있는 경우 탈퇴가 불가능 합니다.`;
    }
    
    rootStore.showMessageDialog(
      message, 
      '확인', 
      function () { router.push('/') }
    );
  } else if (result?.RESULT_CODE === '517') {
    rootStore.showMessageDialog(
      '아래의 사유로<br>탈퇴를 완료하지 못했어요<br>정기배송을 이용 중이거나,<br>결제 건이 존재해요.', 
      '확인', 
      function () { router.push('/') }
    );
  } else if (result?.RESULT_CODE !== '0') {
    rootStore.showMessageDialog(
      '아래의 사유로<br>탈퇴를 완료하지 못했어요<br>정기배송을 이용 중이거나,<br>결제 건이 존재해요.', 
      '확인', 
      function () { router.push('/') }
    );
  }

  //탈퇴 성공시 flarelane 이벤트 전송
  if (result?.RESULT_CODE === '0') {
    const flareLaneUser = {
      member_status: 'withdrawal',
    };
    await flareLaneSetTag(flareLaneUser);
  }
  
  return result?.RESULT_CODE === '0' ? true : false;
};